import { z } from "zod";
import { TIME_GRAINS } from "./model";

export const TimeGrainSchema = z.enum(TIME_GRAINS);
export const FormatSchema = z.enum(["currency", "number", "integer", "percent", "duration"]);
/**
 * A formula's format: any format, or `ratio`, a fraction (0.25) shown as a percent (25%). `percent`
 * expects the value already × 100 (`tips / net_sales * 100`); the planner applies a ratio's × 100.
 */
export const FormulaFormatSchema = z.enum([...FormatSchema.options, "ratio"]);

const refPattern = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
export const RefSchema = z.string().regex(refPattern, "Expected a reference like `model.field`");

export const MetricSelectionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("metric"), ref: RefSchema }),
  z.object({
    kind: z.literal("formula"),
    id: z.string().regex(/^[a-z][a-z0-9_]*$/),
    label: z.string().min(1).max(80),
    expr: z.string().min(1).max(500),
    format: FormulaFormatSchema.optional(),
  }),
]);
export type MetricSelection = z.infer<typeof MetricSelectionSchema>;

export const DimensionSelectionSchema = z.object({
  ref: RefSchema,
  grain: TimeGrainSchema.optional(),
});
export type DimensionSelection = z.infer<typeof DimensionSelectionSchema>;

export const FILTER_OPS = [
  "eq", "neq", "in", "not_in", "gt", "gte", "lt", "lte", "between", "contains", "is_null", "not_null",
] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

export const FilterSchema = z.object({
  ref: RefSchema,
  op: z.enum(FILTER_OPS),
  values: z.array(z.union([z.string().max(200), z.number(), z.boolean()])).max(500).default([]),
});
export type Filter = z.infer<typeof FilterSchema>;

/** Most page-level filters a dashboard adds to each of its widgets. */
export const MAX_DASHBOARD_FILTERS = 20;

/**
 * A page-level filter from a drill-down click, applied to every widget on the dashboard that can
 * take it. `pack` is the data pack its field belongs to (absent: the workspace's first).
 */
export const DashboardFilterSchema = FilterSchema.extend({
  pack: z.string().regex(/^[a-z0-9_]+$/).max(64).optional(),
});
export type DashboardFilter = z.infer<typeof DashboardFilterSchema>;

const isoDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/, "Expected YYYY-MM-DD or YYYY-MM-DD HH:MM[:SS]");

/**
 * Venue-local dates, both included: Oct 19 to Oct 25 is `2026-10-19` to `2026-10-25`. An end with a
 * time (`2026-10-25 14:00`) is an exact instant and stays exclusive.
 */
export const TimeRangeSchema = z.object({ start: isoDateTime, end: isoDateTime });
export type TimeRange = z.infer<typeof TimeRangeSchema>;

/**
 * The last day a range covers, for labels. A date-only end is that day. An end with a time is
 * exclusive, so one at midnight (`2026-10-26 00:00`) stops the day before it, and one later in the
 * day (`2026-10-25 14:00`) is partway through that day.
 */
export function lastDayOf(r: { start: string; end: string }): string {
  const day = r.end.slice(0, 10);
  if (r.end.length <= 10 || !/^00:00(:00)?$/.test(r.end.slice(11))) return day;
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  const prev = d.toISOString().slice(0, 10);
  return prev < r.start.slice(0, 10) ? r.start.slice(0, 10) : prev;
}

/**
 * Relative ranges, resolved on the server against each location's own current
 * (fiscal) day, never the viewer's browser clock. "Last N days" excludes today; "last week" is
 * the previous full Monday–Sunday week.
 */
export const TIME_PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last_7_days", label: "Last 7 days" },
  { id: "last_week", label: "Last week" },
  { id: "last_30_days", label: "Last 30 days" },
  { id: "last_90_days", label: "Last 90 days" },
  { id: "month_to_date", label: "Month to date" },
  { id: "last_month", label: "Last month" },
] as const;
export type TimePreset = (typeof TIME_PRESETS)[number]["id"];
export const TimePresetSchema = z.enum(TIME_PRESETS.map((p) => p.id) as [TimePreset, ...TimePreset[]]);

/**
 * The time part of a widget query: a preset, resolved by the server in each
 * location's own time zone, or a custom absolute range that applies the same
 * dates to every location.
 */
export type TimeSelection =
  | { timePreset: TimePreset; timeRange?: undefined }
  | { timeRange: TimeRange; timePreset?: undefined };

export const asPreset = (p: string | undefined): TimePreset =>
  (TIME_PRESETS.find((x) => x.id === p)?.id ?? "last_30_days");

/** A dashboard's saved date range as a selection. A saved custom range wins over the preset. */
export const asSelection = (f: { timePreset?: string; timeRange?: TimeRange } | undefined): TimeSelection =>
  f?.timeRange ? { timeRange: f.timeRange } : { timePreset: asPreset(f?.timePreset) };

export const MAX_COMPARE_PERIODS = 6;

export const SortSchema = z.object({ key: z.string(), dir: z.enum(["asc", "desc"]) });

export const SemanticQuerySchema = z.object({
  /**
   * The workspace data pack every ref belongs to ("gotab", "fixture"). Absent means the workspace's
   * first pack, so a query never spans packs and queries saved before there were several still run.
   */
  pack: z.string().regex(/^[a-z0-9_]+$/).max(64).optional(),
  metrics: z.array(MetricSelectionSchema).min(1).max(50),
  dimensions: z.array(DimensionSelectionSchema).max(20).default([]),
  filters: z.array(FilterSchema).max(50).default([]),
  /** Absolute range; the same dates apply to every location. Mutually exclusive with `timePreset`. */
  timeRange: TimeRangeSchema.optional(),
  /** Relative range, resolved per location. */
  timePreset: TimePresetSchema.optional(),
  compareTo: z.enum(["previous_period", "previous_year"]).optional(),
  /** How many periods back to compare with (1 = just the one before). Needs `compareTo`. */
  comparePeriods: z.number().int().min(1).max(MAX_COMPARE_PERIODS).optional(),
  /**
   * Show additive metrics as an average per day instead of a total: each row is divided by the
   * number of days in the range that fall into it, so a Monday bucket is divided by the number of
   * Mondays. Evens out groupings whose buckets cover different numbers of days. Needs a date range.
   */
  averagePer: z.enum(["day"]).optional(),
  sort: z.array(SortSchema).max(5).default([]),
  limit: z.number().int().positive().optional(),
  /**
   * Apply `limit` to groups instead of rows: keep the `limit` groups of these dimension keys
   * with the highest (or lowest) total of the metric `by`, and every row inside them. A pivot
   * uses it so "top 20" means the 20 products with the biggest Total, each with all its hours.
   */
  limitBy: z.object({
    dimensions: z.array(z.string()).min(1).max(20),
    by: z.string(),
    dir: z.enum(["asc", "desc"]).default("desc"),
  }).optional(),
});
export type SemanticQuery = z.infer<typeof SemanticQuerySchema>;
export type SemanticQueryInput = z.input<typeof SemanticQuerySchema>;

/** Stable key used for result columns and sorting. */
export function metricKey(m: MetricSelection): string {
  return m.kind === "metric" ? m.ref : `formula.${m.id}`;
}
export function dimensionKey(d: DimensionSelection): string {
  return d.grain ? `${d.ref}:${d.grain}` : d.ref;
}
